import { format, parseISO } from 'date-fns';

interface Comment {
  id: string;
  vote?: 'up' | 'down';
  comment: string;       // Use 'comment' field from API
  timestamp: string;
  page: string;
  country?: string;
}

interface TopCommentsDisplayProps {
  comments: Comment[];
  isLoading?: boolean;
}

export function TopCommentsDisplay({ comments, isLoading }: TopCommentsDisplayProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Sort comments by timestamp (most recent first) and take top 5
  const topComments = comments
    .filter(comment => comment.comment && comment.comment.trim()) // Filter comments with content
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);

  const getSentimentInfo = (vote?: 'up' | 'down') => {
    if (vote === 'up') {
      return {
        icon: '👍',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Positive'
      };
    } else if (vote === 'down') {
      return {
        icon: '👎',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Negative'
      };
    } else {
      return {
        icon: '💬',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        label: 'Neutral'
      };
    }
  };

  const truncatePage = (page: string, maxLength: number = 40) => {
    if (page.length <= maxLength) return page;
    return `${page.substring(0, maxLength)}...`;
  };

  const getCommentLength = (comment: string) => {
    if (comment.length > 100) return { label: 'Long', color: 'text-blue-600' };
    if (comment.length > 50) return { label: 'Medium', color: 'text-yellow-600' };
    return { label: 'Short', color: 'text-gray-600' };
  };

  const positiveComments = topComments.filter(c => c.vote === 'up').length;
  const negativeComments = topComments.filter(c => c.vote === 'down').length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Recent Feedback Comments</h3>
          <p className="text-sm text-gray-600">Latest customer insights and feedback</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-yellow-600">💬</div>
          <div className="text-xs text-gray-500 mt-1">
            {comments.length} total
          </div>
        </div>
      </div>

      {topComments.length > 0 ? (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{topComments.length}</div>
              <div className="text-xs text-gray-500">Shown</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{positiveComments}</div>
              <div className="text-xs text-gray-500">Positive</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">{negativeComments}</div>
              <div className="text-xs text-gray-500">Negative</div>
            </div>
          </div>

          {/* Comments List */}
          <div className="space-y-3">
            {topComments.map((comment, index) => {
              const sentiment = getSentimentInfo(comment.vote);
              const commentLength = getCommentLength(comment.comment);
              
              return (
                <div
                  key={comment.id}
                  className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${sentiment.borderColor} ${sentiment.bgColor}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{sentiment.icon}</span>
                      <span className={`text-sm font-medium ${sentiment.color}`}>
                        {sentiment.label}
                      </span>
                      <span className="text-xs text-gray-400">#{index + 1}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        {format(parseISO(comment.timestamp), 'MMM dd, HH:mm')}
                      </div>
                      <div className={`text-xs ${commentLength.color}`}>
                        {commentLength.label} comment
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-gray-900 text-sm leading-relaxed break-words">
                      &quot;{comment.comment}&quot;
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <span>📄</span>
                      <span title={comment.page} className="truncate max-w-xs">
                        {truncatePage(comment.page)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {comment.comment.length} chars
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {comments.length > 5 && (
            <div className="text-center pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing 5 of {comments.length} comments
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Visit the full comments section to view all feedback
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">💬</div>
          <p className="text-gray-500">No comments available yet</p>
          <p className="text-sm text-gray-400">Comments will appear as customers provide feedback</p>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600">
              💡 <strong>Tip:</strong> Comments provide valuable insights into user experience. 
              Encourage customers to leave feedback to improve your product!
            </p>
          </div>
        </div>
      )}
    </div>
  );
} 